import { differenceInHours } from 'date-fns';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Flooding } from 'features/flooding/flooding.types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { NotificationType } from 'features/notifications/notifications.constants';
import { MapIcon } from 'features/mapping/mapping.types';
import * as wildfireIcons from './iconData/wildfireIcons';
import { cameraIcons } from './iconData/cameraIcons';
import {
  ComplexFireIcon,
  InactiveComplexFireIcon,
  complexFireIcons,
} from './iconData/complexFireIcons';
import { evacuationIcons } from './iconData/evacuationIcons';
import { externalGeoEventIcons } from './iconData/externalGeoEventIcons';
import { femaFloodHazardIcons } from './iconData/femaFloodHazardIcons';
import { floodingIcons } from './iconData/floodingIcons';
import { hazardIcons } from './iconData/hazardIcons';
import { lightningIcons } from './iconData/lightningIcons';
import { locationIcons } from './iconData/locationIcons';
import { mapMarkerIcons } from './iconData/mapMarkerIcons';
import { patternIcons } from './iconData/patternIcons';
import { placeIcons } from './iconData/placeIcons';
import { powerOutageIcons } from './iconData/powerOutageIcons';
import { radioTowerIcons } from './iconData/radioTowerIcons';
import { reportIcons } from './iconData/reportIcons';
import { warningsAndAlertsIcons } from './iconData/warningsAndAlertsIcons';
import { waterLevelIcons } from './iconData/waterLevelIcons';
import { weatherStationIcons } from './iconData/weatherStationIcons';

export {
  ActiveFireIcon,
  ActiveFire50Icon,
  ActiveFire75Icon,
  ActiveFire100Icon,
  ActivePrescribedFireIcon,
  ContainedFireIcon,
  InactiveFireIcon,
  ScheduledFireIcon,
  SelectedActiveFireIcon,
  SelectedActiveFire50Icon,
  SelectedActiveFire75Icon,
  SelectedActiveFire100Icon,
  SelectedActivePrescribedFireIcon,
  SelectedInactiveFireIcon,
  SelectedInactivePrescribedFireIcon,
  SelectedScheduledFireIcon,
  SelectedSilentActiveFireIcon,
  SelectedSilentInactiveFireIcon,
  SilentActiveFireIcon,
  SilentInactiveFireIcon,
} from './iconData/wildfireIcons';

// Map icons with Image rasters for maplibre (wildfire pins selected via getMapIconsWithRasters).
export const nonWildfireMapIcons: MapIcon[] = [
  ...cameraIcons,
  ...complexFireIcons,
  ...evacuationIcons,
  ...externalGeoEventIcons,
  ...femaFloodHazardIcons,
  ...floodingIcons,
  ...hazardIcons,
  ...lightningIcons,
  ...locationIcons,
  ...mapMarkerIcons,
  ...patternIcons,
  ...placeIcons,
  ...powerOutageIcons,
  ...radioTowerIcons,
  ...reportIcons,
  ...warningsAndAlertsIcons,
  ...waterLevelIcons,
  ...weatherStationIcons,
];

const getHoursSince = (date: string | Date): number =>
  differenceInHours(new Date(), new Date(date));

export const getReportMapVisibilityHours = (
  geoEventType?: GeoEventType,
): number =>
  // these values mirror the values in the backend GEO_EVENT_TYPE_REPORT_LOOKBACK_HOURS
  geoEventType === 'flooding' || geoEventType === 'hazard' ? 12 : 6;

/**
 * Fade report icon based upon age
 */
export const getOpacityForReportMarker = (
  date?: string,
  isFadable?: boolean,
  maxAgeHours: number = 6,
): number => {
  if (!date || !isFadable) {
    return 1;
  }

  const diffHours = getHoursSince(date);
  if (diffHours >= maxAgeHours) {
    return 0;
  }
  if (diffHours >= maxAgeHours * (2 / 3)) {
    return 0.5;
  }
  if (diffHours >= maxAgeHours / 2) {
    return 0.7;
  }
  if (diffHours >= maxAgeHours / 3) {
    return 0.85;
  }
  return 1;
};

/**
 * Return the appropriate icon for a wildfire.
 * @param {geoEvent} geoEvent
 * @returns object
 */
export {
  getWildfireMapRasterIcons,
  WILDFIRE_MAP_RASTER_ICON_NAMES,
} from './wildfireIcons.utils';

export const getWildfireGeoEventIcon = (
  geoEvent: Partial<Wildfire>,
): MapIcon => {
  const { isActive = true, notificationType, data } = geoEvent;

  const {
    isComplexParent,
    isPrescribed = true,
    isFps = false,
    manualDeactivationStarted = false,
    prescribedDateStart,
  } = (data as Partial<Wildfire['data']>) ?? {};
  const isScheduled = Boolean(
    isPrescribed &&
      prescribedDateStart &&
      new Date(prescribedDateStart) > new Date(),
  );
  const containment = (data as Partial<Wildfire['data']>)?.containment ?? 0;

  if (isComplexParent) {
    return isActive ? ComplexFireIcon : InactiveComplexFireIcon;
  }

  if (notificationType === NotificationType.SILENT) {
    if (isScheduled) {
      return wildfireIcons.ScheduledFireIcon;
    }
    if (isPrescribed) {
      return wildfireIcons.ActivePrescribedFireIcon;
    }
    if (isActive) {
      return wildfireIcons.SilentActiveFireIcon;
    }
    return wildfireIcons.SilentInactiveFireIcon;
  }

  if (!isActive) {
    return wildfireIcons.InactiveFireIcon;
  }

  if (containment === 100) {
    return wildfireIcons.ActiveFire100Icon;
  }
  if (containment >= 75) {
    return wildfireIcons.ActiveFire75Icon;
  }

  if (containment >= 50) {
    return wildfireIcons.ActiveFire50Icon;
  }

  if (isFps || manualDeactivationStarted) {
    return wildfireIcons.ActiveFire50Icon;
  }

  return wildfireIcons.ActiveFireIcon;
};

/**
 * Fade incident icon based upon last modified, but only when the incident is inactive.
 * @param {geoEvent} geoEvent
 * @param {boolean} isFadable
 * @returns integer
 */
const getBaseOpacityForGeoEventMarker = (args: {
  geoEvent: Partial<Wildfire | Flooding>;
  isFadable: boolean;
  isHistorical: boolean;
}): number => {
  const { geoEvent, isFadable, isHistorical } = args;
  const { isActive, dateModified, data, geoEventType } = geoEvent;

  if (!isFadable || isActive || !dateModified) {
    return 1;
  }

  // Fading logic for flooding has not been defined
  if (geoEventType === 'flooding') {
    return 1;
  }

  // For wildfire, check if prescribed for different opacity
  if (
    geoEventType === 'wildfire' &&
    (data as Partial<Wildfire['data']>)?.isPrescribed
  ) {
    return 0.6;
  }

  const diffHours = getHoursSince(dateModified);
  let val = 1;
  if (diffHours >= 24) {
    val = isHistorical ? 0.4 : 0;
  } else if (diffHours >= 12) {
    val = 0.6;
  } else if (diffHours >= 6) {
    val = 0.7;
  } else if (diffHours >= 3) {
    val = 0.8;
  }
  return val;
};

export const getOpacityForGeoEventMarker = (args: {
  geoEvent: Partial<Wildfire | Flooding>;
  isFadable: boolean;
  isFilteredOut?: boolean;
  isHistorical?: boolean;
}): number => {
  const {
    geoEvent,
    isFadable,
    isFilteredOut = false,
    isHistorical = false,
  } = args;

  const opacity = getBaseOpacityForGeoEventMarker({
    geoEvent,
    isFadable,
    isHistorical,
  });

  // Incidents that are inactive are de-emphasized, but never made more visible than they would be otherwise... so
  // a stale inactive fire that has already faded out stays hidden.
  return isFilteredOut ? Math.min(0.5, opacity) : opacity;
};

/**
 * Based upon acreage we will return the proper size to scale an icon for wildfire
 *
 * @param {geoEvent} geoEvent
 * @returns float
 */
export const getWildfireEventScale = (
  geoEvent: Partial<Wildfire>,
  isFilteredOut = false,
): number => {
  const { data, notificationType, isActive } = geoEvent;

  const acreage = (data as Partial<Wildfire['data']>)?.acreage ?? 0;
  let val = 0.7; // our default size
  if ((data as Partial<Wildfire['data']>)?.isComplexParent) {
    return val;
  }
  if (
    (data as Partial<Wildfire['data']>)?.isPrescribed ||
    notificationType === NotificationType.SILENT
  ) {
    return 0.6;
  }
  if (isFilteredOut) {
    return 0.7;
  }
  if (acreage >= 50000) {
    val = 1.5;
  } else if (acreage >= 20000) {
    val = 1.4;
  } else if (acreage >= 10000) {
    val = 1.3;
  } else if (acreage >= 5000) {
    val = 1.2;
  } else if (acreage >= 1000) {
    val = 1.1;
  } else if (acreage >= 500) {
    val = 1.05;
  } else if (acreage >= 100) {
    val = 1.0;
  } else if (acreage >= 50) {
    val = 0.9;
  } else {
    val = 0.8;
  }
  if (!isActive) {
    return 0.8;
  }

  return val;
};
